import React from "react";
import { X } from "lucide-react";

import { VbButton } from "@/components/vb";

type Variant = "warning" | "info" | "success" | "error" | "neutral" | "trial";

type Action = {
    label: string;
    onClick: () => void;
};

export type BannerProps = {
    open?: boolean;
    defaultOpen?: boolean;
    onClose?: () => void;
    variant?: Variant;
    children: React.ReactNode;
    action?: Action;
    sticky?: boolean;
    className?: string;
    positionClass?: string;
    isFixed?: boolean;
    dismissible?: boolean;
};

/**
 * Tone map — surface / ink / hairline triplets taken straight off the status
 * palette, mirroring `.vb-system-banner` so the two top-of-app bars read as one
 * family. `warning` is the loud tone (payment action required); `trial` is the
 * quiet one on the same amber, so a countdown never shouts like a failure.
 *
 * Only `warning` and `trial` have call sites today (Layout + TrialBanner); the
 * other four are kept because dropping them would narrow the public variant
 * contract.
 */
const VARIANT_STYLES: Record<Variant, string> = {
    warning:
        "bg-vb-warn-bg text-vb-warn-ink border-vb-warn-line font-semibold",
    info:
        "bg-vb-brand-soft text-vb-brand-pressed border-vb-brand-line font-semibold",
    success:
        "bg-vb-pass-bg text-vb-pass-ink border-vb-pass-line font-semibold",
    error:
        "bg-vb-fail-bg text-vb-fail-ink border-vb-fail-line font-semibold",
    neutral:
        "bg-vb-bg-subtle text-vb-text border-vb-line font-semibold",
    // Same amber as `warning`; the prototype's `.trial-band` is weight 600 like
    // the rest, so it carries the bold too.
    trial:
        "bg-vb-warn-bg text-vb-warn-ink border-vb-warn-line font-semibold",
};

export function Banner({
    open,
    defaultOpen = true,
    onClose,
    variant = "warning",
    children,
    action,
    sticky = true,
    className = "",
    positionClass = "top-0 inset-x-0",
    isFixed = true,
    dismissible = true,
}: BannerProps) {
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
    const isControlled = typeof open === "boolean";
    const isOpen = isControlled ? (open as boolean) : internalOpen;

    const barRef = React.useRef<HTMLDivElement | null>(null);

    // Only runs for a fixed+sticky bar, which no current call site asks for —
    // both pass sticky={false} isFixed={false}. Kept because the props are
    // public and a future caller can still turn it on.
    React.useLayoutEffect(() => {
        if (!sticky || !isFixed) return;
        const updatePadding = () => {
            if (isOpen && barRef.current) {
                const h = barRef.current.getBoundingClientRect().height;
                document.body.style.paddingTop = `${h}px`;
            } else {
                document.body.style.paddingTop = "";
            }
        };
        updatePadding();
        const ro = new ResizeObserver(updatePadding);
        if (barRef.current) ro.observe(barRef.current);
        return () => {
            ro.disconnect();
            document.body.style.paddingTop = "";
        };
    }, [isOpen, sticky]);

    const close = () => {
        if (!isControlled) setInternalOpen(false);
        onClose?.();
    };

    if (!isOpen) return null;

    return (
        <div
            ref={barRef}
            className={[
                isFixed ? "fixed z-50" : "relative w-full",
                positionClass,
                "pointer-events-auto",
                "translate-y-0 transition-transform duration-300 will-change-transform",
            ].join(" ")}

            role={variant === "error" ? "alert" : "status"}
            aria-live={variant === "error" ? "assertive" : "polite"}
        >
            <div
                className={[
                    // Wraps on narrow screens rather than crushing the message:
                    // the copy keeps the row, the action + dismiss drop below it.
                    "flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 md:px-6",
                    "border-b text-[13px] leading-[1.45] md:text-sm",
                    VARIANT_STYLES[variant],
                    className,
                ].join(" ")}
                style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}
            >
                <div className="min-w-0 flex-1">{children}</div>

                {(action || dismissible) && (
                    // One row for both controls — the dismiss used to be
                    // absolutely positioned and sat on top of the action.
                    <div className="ml-auto flex shrink-0 items-center gap-2">
                        {action && (
                            <VbButton
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={action.onClick}
                            >
                                {action.label}
                            </VbButton>
                        )}

                        {dismissible && (
                            <button
                                type="button"
                                onClick={close}
                                aria-label="Dismiss banner"
                                className="grid shrink-0 place-items-center rounded-vb-xs p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-[3px] focus-visible:ring-vb-focus"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
export default Banner;
